import React, { ReactNode } from "react";
import styles from "./DataTable.module.scss";
import Edit from "../../../assets/images/actions/Edit";
import Delete from "../../../assets/images/actions/Delete";
import ArrowLeft from "../../../assets/images/actions/ArrowLeft";
import ArrowRight from "../../../assets/images/actions/ArrowRight";
import Up from "../../../assets/images/table/Up";
import Down from "../../../assets/images/table/Down";
import Loading from "../loading/Loading";
import Detail from "../../../assets/images/actions/Detail";

type RenderFn = (value: any, row?: any, index?: number) => React.ReactNode;

export interface Column {
  key: string;
  header?: string | ReactNode;
  render?: RenderFn;
  filter?: boolean;
}
export interface PaginationData {
  total: number | null;
  page?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

interface DataTableProps {
  data: any[];
  columns?: Column[];
  className?: string;
  titleStyle?: string;
  noDataMessage?: string;
  actions?: boolean;
  onEdit?: (id: number) => void;
  onDetail?: (row: any) => void;
  onDelete?: (id: number, row?: any) => void;
  table_title?: string;
  paginationData?: PaginationData;
  other?: boolean;
  no_borders?: boolean;
  loading?: boolean;
}

export default function DataTable({
  data,
  columns,
  className = "",
  titleStyle = "",
  noDataMessage = "No data",
  actions,
  onEdit,
  onDetail,
  onDelete,
  table_title,
  paginationData,
  other = false,
  no_borders = false,
  loading,
}: DataTableProps) {
  const inferredColumns: Column[] = React.useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (!data || data.length === 0) return [];

    const first = data[0];
    const keys = Object.keys(first);
    return keys.map((k) => ({ key: k, header: k }));
  }, [columns, data]);

  const cols = inferredColumns;

  if (loading) {
    return <Loading />;
  }

  const totalPages = paginationData?.total
    ? Math.ceil(paginationData.total / (paginationData.limit || 10))
    : 1;

  const start =
    ((paginationData?.page || 1) - 1) * (paginationData?.limit || 10) + 1;
  const end = Math.min(
    start + (paginationData?.limit || 10) - 1,
    paginationData?.total || 0,
  );

  return (
    <div
      className={`${styles.data_table} ${no_borders ? styles.no_borders : ""} `}
    >
      <div className={`${styles.wrapper} ${className}`}>
        <div className={styles.header}>
          {table_title && (
            <h1 className={`${styles.table_title} ${titleStyle}`}>
              {table_title}
            </h1>
          )}
          {paginationData &&
            paginationData.total != null &&
            paginationData.total > 10 && (
              <div className={styles.table_paginate}>
                <div className={styles.pagination_pages}>
                  <p className={styles.current_pages}>
                    {start}-{end}
                  </p>
                  <p className={styles.all_pages}>of {paginationData.total}</p>
                </div>
                <p className={styles.page_info}>
                  Page {paginationData.page} of {totalPages}
                </p>
              </div>
            )}
        </div>
        <div className={styles.table_wrapper}>
          <table
            className={`${styles.table} ${other ? styles.other_table : ""} `}
          >
            <thead>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className={`${styles.th} ${other ? styles.other_th : ""}`}
                  >
                    <div className={styles.header_cell}>
                      <div className={styles.key}> {c.header ?? c.key}</div>
                      {c.filter && (
                        <div className={styles.filters}>
                          <div className={styles.filter}>
                            <Up />
                          </div>
                          <div className={styles.filter}>
                            <Down />
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                {actions && (
                  <th
                    className={`${styles.th} ${other ? styles.other_th : ""} `}
                  ></th>
                )}
              </tr>
            </thead>
            <tbody>
              {(!data || data.length === 0) && (
                <tr className={`${styles.tr} ${other ? styles.other_tr : ""}`}>
                  <td
                    className={`${styles.td} ${other ? styles.other_td : ""}`}
                    colSpan={cols.length + (actions ? 1 : 0)}
                  >
                    <div className={styles.noData}>{noDataMessage}</div>
                  </td>
                </tr>
              )}
              {data &&
                data.length > 0 &&
                data.map((row, rowIndex) => (
                  <tr
                    style={{ cursor: onDetail ? "pointer" : "default" }}
                    key={rowIndex}
                    className={`${styles.tr} ${other ? styles.other_tr : ""}`}
                  >
                    {cols.map((c) => {
                      const value = row == null ? undefined : row[c.key];
                      return (
                        <td
                          key={c.key}
                          className={`${styles.td} ${
                            other ? styles.other_td : ""
                          }`}
                        >
                          <div>
                            {c.render
                              ? c.render(value, row, rowIndex)
                              : (value ?? "")}
                          </div>
                        </td>
                      );
                    })}
                    {actions && (
                      <td
                        className={`${styles.td} ${
                          other ? styles.other_td : ""
                        }`}
                      >
                        <div className={styles.icons}>
                          {onDetail && (
                            <div
                              className={styles.icon}
                              onClick={() => onDetail(row)}
                            >
                              <Detail />
                            </div>
                          )}
                          {onEdit && (
                            <div
                              className={styles.icon}
                              onClick={() => onEdit(row.id)}
                            >
                              <Edit />
                            </div>
                          )}
                          {onDelete && (
                            <div
                              className={styles.icon}
                              onClick={() => onDelete(row.id, row)}
                            >
                              <Delete />
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {paginationData &&
          paginationData.total != null &&
          paginationData.total > 10 && (
            <div className={styles.pagination_controls}>
              <div className={styles.page_counts}>
                <p className={styles.count_title}>Rows per page: </p>
                <select
                  className={styles.select}
                  value={paginationData.limit}
                  onChange={(e) =>
                    paginationData.onLimitChange?.(Number(e.target.value))
                  }
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className={styles.arrows}>
                <div
                  className={styles.arrow}
                  onClick={() =>
                    paginationData.onPageChange?.(
                      (paginationData.page || 1) - 1,
                    )
                  }
                >
                  <ArrowLeft />
                </div>
                <div
                  className={styles.arrow}
                  onClick={() =>
                    paginationData.onPageChange?.(
                      (paginationData.page || 1) + 1,
                    )
                  }
                >
                  <ArrowRight />
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
